import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useState, useEffect, useContext, useRef } from "react";
import VideoDisplay from "../components/VideoDisplay";
import NotesDisplay from "../components/NotesDisplay";
import NotificationContext from "../context/NotificationContext";
import { useAuth0 } from "@auth0/auth0-react";
import ProgressOverview from "../components/ProgressOverview";
import { useViewPermission } from "../context/PermissionContext";
import { useMutationLock } from "../hooks/useMutationLock";
import AccessDenied from "../components/AccessDenied";
import LoginRequired from "../components/LoginRequired";
import Loader from "../components/Loader";
import {
  dropdownListBody,
  normalizeListResponse,
} from "../utils/listPagination";
import * as topicsApi from "../api/topics";
import * as lecturesApi from "../api/lectures";
import * as notesApi from "../api/notes";
import * as worksheetsApi from "../api/worksheets";
import * as progressApi from "../api/progress";

export default function SelfLearning() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth0();
  const { subjectId, gradeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const topicIdFromURL = searchParams.get("topicId");

  const notificationCtx = useContext(NotificationContext);
  const { run } = useMutationLock();

  const [topics, setTopics] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [topicSelected, setTopicSelected] = useState(null);
  const [nextClicked, setNextClicked] = useState(false);
  const [markedItems, setMarkedItems] = useState([]);
  const [topicProgress, setTopicProgress] = useState({}); // {topicId: {marked: [], total: 0, percentage: 0}}

  const [lectureItems, setLectureItems] = useState([]);
  const [noteItems, setNoteItems] = useState([]);
  const [worksheetItems, setWorksheetItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [progressLoading, setProgressLoading] = useState(true);

  const { hasPermission, loading: permLoading } =
    useViewPermission("view_self_learning");

  const [overallProgress, setOverallProgress] = useState(0);

  const lectureRef = useRef(null);
  const notesRef = useRef(null);
  const worksheetRef = useRef(null);
  const solutionRef = useRef(null);

  const [activeSection, setActiveSection] = useState(null);

  const scrollToSection = (ref, key) => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // trigger highlight
      setActiveSection(key);

      // remove highlight after short time
      setTimeout(() => {
        setActiveSection(null);
      }, 1000);
    }
  };

  // ✅ Fetch topics
  useEffect(() => {
    if (!subjectId || !gradeId || permLoading || !hasPermission) return;
    const fetchTopics = async () => {
      try {
        const res = await topicsApi.bySubjectAndGrade(
          subjectId,
          gradeId,
          dropdownListBody(),
        );
        setTopics(normalizeListResponse(res.data).data);
      } catch (err) {
        console.error("Error fetching topics:", err);
      }
    };

    fetchTopics();
  }, [subjectId, gradeId, hasPermission, permLoading]);

  // ✅ Fetch progress for all topics
  useEffect(() => {
    if (!hasPermission || !topics.length || !user?.email) return;

    const fetchAllProgress = async () => {
      setProgressLoading(true); // ✅ START LOADING

      const progressData = {};

      await Promise.all(
        topics.map(async (topic) => {
          try {
            const topicId = topic.id;

            const [progressRes, lecturesRes, notesRes, sheetsRes] =
              await Promise.allSettled([
                progressApi.getByTopic(topicId),
                lecturesApi.byTopic(topicId),
                notesApi.byTopic(topicId),
                worksheetsApi.byTopic(topicId),
              ]);

            const marked =
              progressRes.status === "fulfilled"
                ? progressRes.value.data.items_marked || []
                : [];

            const lectureItems =
              lecturesRes.status === "fulfilled"
                ? lecturesRes.value.data?.items || []
                : [];

            const noteItems =
              notesRes.status === "fulfilled"
                ? notesRes.value.data?.items || []
                : [];

            const worksheetItems =
              sheetsRes.status === "fulfilled"
                ? sheetsRes.value.data?.items || []
                : [];

            // Exclude solutions from progress denominator
            const total =
              lectureItems.length + noteItems.length + worksheetItems.length;

            const itemKeys = [
              ...lectureItems.map((item) => `lectureItem:${item.id}`),
              ...noteItems.map((item) => `noteItem:${item.id}`),
              ...worksheetItems.map((item) => `worksheetItem:${item.id}`),
            ];
            const markedCount = marked.filter((key) =>
              itemKeys.includes(key),
            ).length;

            const percentage =
              total > 0 ? Math.round((markedCount / total) * 100) : 0;

            progressData[topicId] = {
              marked,
              total,
              percentage,
            };
          } catch {
            progressData[topic.id] = {
              marked: [],
              total: 0,
              percentage: 0,
            };
          }
        }),
      );

      setTopicProgress(progressData);
      setProgressLoading(false); // ✅ END LOADING
    };

    fetchAllProgress();
  }, [topics, user?.email, hasPermission]);

  useEffect(() => {
    if (!topics.length || !Object.keys(topicProgress).length) return;

    let totalWeight = 0;
    let weightedSum = 0;

    topics.forEach((t) => {
      const weight = t.weightage || 1;
      const progress = topicProgress[t.id]?.percentage || 0;

      totalWeight += weight;
      weightedSum += progress * weight;
    });

    const overall = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0;

    setOverallProgress(overall);
  }, [topics, topicProgress]);

  // ✅ Handle URL topic selection
  useEffect(() => {
    if (!hasPermission) return;
    if (topics.length === 0) return;

    if (topicIdFromURL) {
      const found = topics.find((t) => t.id === topicIdFromURL);
      if (found) {
        setTopicSelected(found.id);
        setNextClicked(true);
        return;
      }
    }

    // fallback → auto select first topic
    // if (!topicSelected) {
    //   setTopicSelected(topics[0]?.id);
    // }
  }, [topicIdFromURL, topics, hasPermission]);

  // ✅ Fetch content
  useEffect(() => {
    if (!hasPermission) return;
    if (!topicSelected) return;

    const fetchAll = async () => {
      try {
        setLoading(true);

        setLectureItems([]);
        setNoteItems([]);
        setWorksheetItems([]);

        try {
          const lectures = await lecturesApi.byTopic(topicSelected);
          setLectureItems(lectures?.data?.items || []);
        } catch {
          setLectureItems([]);
        }

        try {
          const notes = await notesApi.byTopic(topicSelected);
          setNoteItems(notes?.data?.items || []);
        } catch {
          setNoteItems([]);
        }

        try {
          const sheets = await worksheetsApi.byTopic(topicSelected);
          setWorksheetItems(sheets?.data?.items || []);
        } catch {
          setWorksheetItems([]);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [topicSelected, hasPermission]);

  // ✅ Set marked items for selected topic
  useEffect(() => {
    if (!topicSelected || !topicProgress[topicSelected]) return;
    const marked = topicProgress[topicSelected].marked || [];
    setMarkedItems(Array.isArray(marked) ? marked : []);
  }, [topicSelected, topicProgress]);

  const handleMarkAsDone = async (itemUrl, isDone) => {
    await run(async () => {
      try {
        if (isDone) {
          // Mark as done - add to progress
          await progressApi.add({
            topicId: topicSelected,
            item: itemUrl,
          });

          setMarkedItems((prev) =>
            prev.includes(itemUrl) ? prev : [...prev, itemUrl],
          );

          // Update topicProgress for real-time UI updates
          setTopicProgress((prev) => {
            const current = prev[topicSelected] || { marked: [], total: 0 };
            const newMarked = current.marked.includes(itemUrl)
              ? current.marked
              : [...current.marked, itemUrl];
            const liveKeys = new Set([
              ...lectureItems.map((item) => `lectureItem:${item.id}`),
              ...noteItems.map((item) => `noteItem:${item.id}`),
              ...worksheetItems.map((item) => `worksheetItem:${item.id}`),
            ]);
            const liveMarkedCount = newMarked.filter((key) =>
              liveKeys.has(key),
            ).length;
            const newPercentage =
              current.total > 0
                ? Math.round((liveMarkedCount / current.total) * 100)
                : 0;
            return {
              ...prev,
              [topicSelected]: {
                ...current,
                marked: newMarked,
                percentage: newPercentage,
              },
            };
          });
        } else {
          // Unmark as done - remove from progress
          await progressApi.removeItem({
            topicId: topicSelected,
            item: itemUrl,
          });

          setMarkedItems((prev) => prev.filter((i) => i !== itemUrl));

          // Update topicProgress for real-time UI updates
          setTopicProgress((prev) => {
            const current = prev[topicSelected] || { marked: [], total: 0 };
            const newMarked = current.marked.filter((i) => i !== itemUrl);
            const liveKeys = new Set([
              ...lectureItems.map((item) => `lectureItem:${item.id}`),
              ...noteItems.map((item) => `noteItem:${item.id}`),
              ...worksheetItems.map((item) => `worksheetItem:${item.id}`),
            ]);
            const liveMarkedCount = newMarked.filter((key) =>
              liveKeys.has(key),
            ).length;
            const newPercentage =
              current.total > 0
                ? Math.round((liveMarkedCount / current.total) * 100)
                : 0;
            return {
              ...prev,
              [topicSelected]: {
                ...current,
                marked: newMarked,
                percentage: newPercentage,
              },
            };
          });
        }

        notificationCtx.show({
          message: isDone ? "Marked as completed" : "Marked as incomplete",
          stat: "success",
        });
      } catch {
        notificationCtx.show({
          message: "Error updating progress",
          stat: "error",
        });
      }
    });
  };

  const solutionItems = worksheetItems.filter((item) => item.solutionUrl);
  const hasContent =
    lectureItems.length > 0 ||
    noteItems.length > 0 ||
    worksheetItems.length > 0 ||
    solutionItems.length > 0;

  return (
    <div>
      <Navbar />

      {authLoading || (isAuthenticated && permLoading) ? (
        <div style={{ paddingTop: 120 }} className="text-center">
          <Loader />
        </div>
      ) : !isAuthenticated ? (
        <LoginRequired message="Please sign in to access self-learning materials." />
      ) : !hasPermission ? (
        <AccessDenied />
      ) : (
        <>
          {/* HEADER */}
          <div className="ms-5 mt-3" style={{ maxWidth: "35vw" }}>
            <div
              style={{
                padding: "4px 14px",
                background: "#3498db",
                color: "#fff",
                borderRadius: "999px",
                display: "inline-block",
                fontSize: "0.8rem",
                fontWeight: "bold",
              }}
            >
              {topics[0]?.gradeName}
            </div>

            <h3 className="mt-1 fw-bold">{topics[0]?.subjectName}</h3>

            {/* OVERALL PROGRESS */}
            <div style={{ marginTop: "10px" }}>
              {/* Label Row */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "4px",
                }}
              >
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "#64748b",
                    fontWeight: "500",
                  }}
                >
                  Overall Progress
                </span>

                {progressLoading ? (
                  <>
                    {/* Skeleton Text */}
                    <div
                      style={{
                        width: "60px",
                        height: "10px",
                        background: "#e2e8f0",
                        borderRadius: "6px",
                        marginBottom: "6px",
                        animation: "pulse 1.2s infinite",
                      }}
                    />
                  </>
                ) : (
                  <>
                    {/* Actual Progress */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          color: "#2c3e50",
                        }}
                      >
                        {overallProgress}%
                      </span>

                      {overallProgress === 100 && (
                        <span style={{ color: "#27ae60", fontSize: "0.9rem" }}>
                          ✓
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Progress Bar */}
              <div
                style={{
                  width: "100%",
                  height: "6px",
                  background: "#e2e8f0",
                  borderRadius: "999px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${overallProgress}%`,
                    height: "100%",
                    borderRadius: "999px",

                    background:
                      overallProgress <= 20
                        ? "#e74c3c"
                        : overallProgress <= 40
                          ? "#e67e22"
                          : overallProgress <= 70
                            ? "#f1c40f"
                            : overallProgress <= 90
                              ? "#2ecc71"
                              : "#27ae60",

                    transition: "width 1.0s ease, background 0.5s ease",
                  }}
                />
              </div>
            </div>
          </div>

          {/* BUTTONS */}
          <div
            className="d-none d-md-flex"
            style={{
              position: "fixed",
              top: "12vh",
              right: "30px",
              zIndex: 20,
              gap: "10px",
              alignItems: "center",
            }}
          >
            {/* SECTION NAV BUTTONS */}
            {nextClicked && !loading && (
              <div style={{ display: "flex", gap: "6px" }}>
                {[
                  lectureItems.length > 0 && {
                    label: "Lectures",
                    ref: lectureRef,
                  },
                  noteItems.length > 0 && {
                    label: "Lecture Notes",
                    ref: notesRef,
                  },
                  worksheetItems.length > 0 && {
                    label: "Worksheets",
                    ref: worksheetRef,
                  },
                  solutionItems.length > 0 && {
                    label: "Solutions",
                    ref: solutionRef,
                  },
                ]
                  .filter(Boolean) // removes false values
                  .map((btn) => (
                    <button
                      key={btn.label}
                      onClick={() => scrollToSection(btn.ref, btn.label)}
                      style={{
                        border: "none",
                        borderRadius: "999px",
                        padding: "8px 18px",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "linear-gradient(135deg, #f1f5f9, #e2e8f0)",
                        color: "#2c3e50",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        transition: "all 0.25s ease",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = nextClicked
                          ? "0 4px 10px rgba(0,0,0,0.12)"
                          : "0 10px 22px rgba(52, 152, 219, 0.45)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = nextClicked
                          ? "0 2px 6px rgba(0,0,0,0.08)"
                          : "0 6px 18px rgba(52, 152, 219, 0.35)";
                      }}
                    >
                      {btn.label}
                    </button>
                  ))}
              </div>
            )}
            {/* BACK BUTTON */}
            <button
              onClick={() => {
                if (!nextClicked) {
                  setTopicSelected(topicSelected || topics[0]?.id);
                  setNextClicked(true);
                } else {
                  setNextClicked(false);
                  setTopicSelected(null);
                  navigate(`/explore/${subjectId}/${gradeId}`, {
                    replace: true,
                  });
                }
              }}
              style={{
                border: "none",
                borderRadius: "999px",
                padding: "8px 18px",
                fontSize: "0.9rem",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: nextClicked
                  ? "linear-gradient(135deg, #f1f5f9, #e2e8f0)"
                  : "linear-gradient(135deg, #3498db, #5dade2)",
                color: nextClicked ? "#2c3e50" : "#ffffff",
                boxShadow: nextClicked
                  ? "0 2px 6px rgba(0,0,0,0.08)"
                  : "0 6px 18px rgba(52, 152, 219, 0.35)",
                transition: "all 0.25s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = nextClicked
                  ? "0 4px 10px rgba(0,0,0,0.12)"
                  : "0 10px 22px rgba(52, 152, 219, 0.45)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = nextClicked
                  ? "0 2px 6px rgba(0,0,0,0.08)"
                  : "0 6px 18px rgba(52, 152, 219, 0.35)";
              }}
            >
              {!nextClicked ? (
                <>
                  Start Learning <span style={{ fontSize: "1rem" }}>→</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: "1rem" }}>←</span> Back
                </>
              )}
            </button>
          </div>

          {!nextClicked ? (
            <ProgressOverview
              topics={topics}
              topicProgress={topicProgress}
              progressLoading={progressLoading}
              handleExploreCLicked={(id) => {
                setTopicSelected(id);
                setNextClicked(true);
              }}
            />
          ) : (
            <div
              className="row px-4 mt-3"
              style={{
                height: "75vh",
                // overflow: "hidden", // prevent page scroll
              }}
            >
              {/* LEFT PANEL */}
              <div
                className="col-md-3"
                style={{
                  // height: "100%",
                  borderRight: "1px solid #e6ecf0",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    overflowY: "auto", // ✅ independent scroll
                    paddingRight: "6px",
                  }}
                >
                  <div
                    style={{
                      position: "sticky",
                      // top: "15vh",
                      maxHeight: "70vh",
                      overflowY: "auto",
                    }}
                  >
                    {topics.map((t, i) => {
                      const weightage = t.weightage || 1;

                      return (
                        <div
                          key={t.id}
                          onClick={() => setTopicSelected(t.id)}
                          onMouseEnter={() => setHoveredCard(i)}
                          onMouseLeave={() => setHoveredCard(null)}
                          style={{
                            padding: "12px 14px",
                            marginBottom: "10px",
                            borderRadius: "12px",
                            cursor: "pointer",
                            background:
                              topicSelected === t.id
                                ? "linear-gradient(135deg, #d6eaff, #edf6ff)"
                                : hoveredCard === i
                                  ? "#f5f9ff"
                                  : "#ffffff",
                            border:
                              topicSelected === t.id
                                ? "1px solid #3498db"
                                : "1px solid #eef2f5",
                            transition: "all 0.25s ease",
                          }}
                        >
                          {/* Topic Name */}
                          <div
                            style={{
                              fontWeight:
                                topicSelected === t.id ? "600" : "500",
                              fontSize: "0.95rem",
                              color: "#2c3e50",
                              marginBottom: "6px",
                            }}
                          >
                            {t.topicName}
                          </div>

                          {/* Difficulty Bars */}
                          <div
                            style={{
                              marginTop: "6px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "10px",
                            }}
                          >
                            {/* Label */}
                            <span
                              style={{
                                fontSize: "0.75rem",
                                color: "#94a3b8",
                                fontWeight: 500,
                              }}
                            >
                              Difficulty
                            </span>

                            {/* Bars */}
                            <div style={{ display: "flex", gap: "4px" }}>
                              {[...Array(5)].map((_, i) => (
                                <div
                                  key={i}
                                  style={{
                                    width: "14px",
                                    height: "5px",
                                    borderRadius: "4px",
                                    background:
                                      i < weightage
                                        ? weightage <= 2
                                          ? "#2ecc71" // easy (green)
                                          : weightage === 3
                                            ? "#f1c40f" // medium (yellow)
                                            : "#e74c3c" // hard (red)
                                        : "#e2e8f0",
                                  }}
                                />
                              ))}
                            </div>
                          </div>

                          {/* Progress Bar */}
                          {(() => {
                            const progress = topicProgress[t.id] || {
                              marked: [],
                              total: 0,
                              percentage: 0,
                            };
                            const progressPercentage = progress.percentage;

                            return (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.7rem",
                                      color: "#94a3b8",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Progress
                                  </span>
                                  {progressLoading ? (
                                    <>
                                      {/* Skeleton Text */}
                                      <div
                                        style={{
                                          width: "60px",
                                          height: "10px",
                                          background: "#e2e8f0",
                                          borderRadius: "6px",
                                          marginBottom: "6px",
                                          animation: "pulse 1.2s infinite",
                                        }}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      {/* Actual Progress */}
                                      <div
                                        style={{
                                          display: "flex",
                                          fontSize: "0.75rem",
                                          color: "#64748b",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                          }}
                                        >
                                          <span>{progress.percentage}%</span>

                                          {progress.percentage === 100 && (
                                            <span
                                              style={{
                                                fontSize: "1rem",
                                                color: "#27ae60",
                                                fontWeight: "bold",
                                              }}
                                            >
                                              ✓
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                                <div style={{ display: "flex", gap: "1px" }}>
                                  {[...Array(10)].map((_, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        flex: 1,
                                        height: "3px",
                                        borderRadius: "2px",
                                        background:
                                          idx < progressPercentage / 10
                                            ? progressPercentage <= 20
                                              ? "#e74c3c" // red
                                              : progressPercentage <= 40
                                                ? "#e67e22" // orange
                                                : progressPercentage <= 70
                                                  ? "#f1c40f" // yellow
                                                  : progressPercentage <= 90
                                                    ? "#2ecc71" // light green
                                                    : "#27ae60" // strong green
                                            : "#e2e8f0",
                                      }}
                                    />
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div
                className="col-md-9"
                style={{
                  height: "100%",
                  overflowY: "auto", // ✅ independent scroll
                  paddingRight: "10px",
                }}
              >
                {loading ? (
                  <h4 className="text-center" style={{ color: "#7f8c8d" }}>
                    Thinking...
                  </h4>
                ) : topicSelected ? (
                  <>
                    {lectureItems.length > 0 && (
                      <div
                        ref={lectureRef}
                        style={{
                          background:
                            activeSection === "Lectures"
                              ? "rgba(52,152,219,0.04)"
                              : "#ffffff",
                          borderRadius: "16px",
                          padding: "16px",
                          marginBottom: "20px",
                          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                          border:
                            activeSection === "Lectures"
                              ? "1px solid #3498db"
                              : "1px solid #eef2f5",
                          transition: "all 0.35s ease",
                        }}
                      >
                        <h5
                          style={{
                            fontWeight: "700",
                            marginBottom: "12px",
                            color: "#2c3e50",
                          }}
                        >
                          Lectures
                        </h5>
                        <VideoDisplay
                          items={lectureItems}
                          progressKeyPrefix="lectureItem"
                          handleMarkAsDone={handleMarkAsDone}
                          markedItems={markedItems}
                        />
                      </div>
                    )}

                    {noteItems.length > 0 && (
                      <div
                        ref={notesRef}
                        style={{
                          background:
                            activeSection === "Lecture Notes"
                              ? "rgba(52,152,219,0.04)"
                              : "#ffffff",
                          borderRadius: "16px",
                          padding: "16px",
                          marginBottom: "20px",
                          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                          border:
                            activeSection === "Lecture Notes"
                              ? "1px solid #3498db"
                              : "1px solid #eef2f5",
                          transition: "all 0.35s ease",
                        }}
                      >
                        <h5
                          style={{
                            fontWeight: "700",
                            marginBottom: "12px",
                            color: "#2c3e50",
                          }}
                        >
                          Lecture Notes
                        </h5>

                        <NotesDisplay
                          items={noteItems}
                          progressKeyPrefix="noteItem"
                          handleMarkAsDone={handleMarkAsDone}
                          markedItems={markedItems}
                        />
                      </div>
                    )}

                    {worksheetItems.length > 0 && (
                      <div
                        ref={worksheetRef}
                        style={{
                          background:
                            activeSection === "Worksheets"
                              ? "rgba(52,152,219,0.04)"
                              : "#ffffff",
                          borderRadius: "16px",
                          padding: "16px",
                          marginBottom: "20px",
                          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                          border:
                            activeSection === "Worksheets"
                              ? "1px solid #3498db"
                              : "1px solid #eef2f5",
                          transition: "all 0.35s ease",
                        }}
                      >
                        <h5
                          style={{
                            fontWeight: "700",
                            marginBottom: "12px",
                            color: "#2c3e50",
                          }}
                        >
                          Worksheets
                        </h5>
                        <NotesDisplay
                          items={worksheetItems}
                          urlField="worksheetUrl"
                          descriptionField="sheetDescription"
                          progressKeyPrefix="worksheetItem"
                          handleMarkAsDone={handleMarkAsDone}
                          markedItems={markedItems}
                        />
                      </div>
                    )}

                    {solutionItems.length > 0 && (
                      <div
                        ref={solutionRef}
                        style={{
                          background:
                            activeSection === "Solutions"
                              ? "rgba(52,152,219,0.04)"
                              : "#ffffff",
                          borderRadius: "16px",
                          padding: "16px",
                          marginBottom: "20px",
                          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                          border:
                            activeSection === "Solutions"
                              ? "1px solid #3498db"
                              : "1px solid #eef2f5",
                          transition: "all 0.35s ease",
                        }}
                      >
                        <h5
                          style={{
                            fontWeight: "700",
                            marginBottom: "12px",
                            color: "#2c3e50",
                          }}
                        >
                          Solutions
                        </h5>
                        <NotesDisplay
                          items={solutionItems}
                          urlField="solutionUrl"
                          descriptionField="solutionDescription"
                          progressKeyPrefix="worksheetItem"
                        />
                      </div>
                    )}
                    {/* If no content available */}
                    {!hasContent && (
                      <div className="text-center">
                        <h6 style={{ color: "#94a3b8" }}>
                          No learning material available for this topic yet.
                        </h6>
                      </div>
                    )}
                  </>
                ) : (
                  <h4 className="text-center">Select a topic to begin</h4>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
